// English translations
const en = {
    // App Header
    appTitle: "AI Pricing Optimization Engine",

    // Language selector
    language: "Language",
    english: "English",
    spanish: "Spanish",

    // Sidebar Menu
    menu: {
        // Plataforma nueva (src/nueva)
        noDictionary: "No data dictionary configured",
        home: "Home",
        mediaMonitoring: "Media Monitoring",
        priceMonitor: "Price Monitor",
        priceAlerts: "Price Alerts",
        opportunities: "Commercial Opportunities",
        chat: "Conversations",
        aiAssistant: "AI Assistant",
        newChat: "New Query",
        reports: "Reports",
        analytics: "Analytics",
        admin: "Admin",
        administration: "Admin Panel",
        settings: "Settings",
        help: "Help",
        faq: "FAQ",
        guide: "Suggested Queries",
        contact: "Contact",
        terms: "Terms & Conditions",
        logout: "Logout",
        moduleDisabled: "This module is not available for your account",
        recentChats: "Recent Chats",
        noChats: "No conversations",
        savedQueries: "Saved Queries",
        noSavedQueries: "No saved queries",
        customReports: "Custom Reports",
        newReport: "New report",
        noReports: "No reports yet",
        deleteReport: "Delete this report?",
        dataDictionary: "Data Dictionary",
        uploadedFiles: "Uploaded Files",
        uploadFile: "Upload File",
        noFiles: "No uploaded files",
        confirmDelete: "Confirm deletion",
        deleteConversation: "Do you want to delete this conversation?",
        deleteSavedQuery: "Do you want to delete this saved query?",
        agentMemory: "Agent Memory",
        agentMemoryTitle: "Custom Instructions",
        agentMemoryDescription: "Write instructions that the agent will remember across all your conversations. For example: preferences, business context, response format, etc.",
        agentMemoryPlaceholder: "Example:\n- Always respond in English\n- I work in the pharmaceutical sector\n- I prefer prices in USD\n- My main competitors are: Walmart, Target, CVS",
        agentMemoryTip: "You can also use '/recuerda' in the chat for the agent to automatically add information.",
        goToAdmin: "Go to Admin Panel",
        interactiveGuide: "Interactive Guide",
        editTitle: "Edit title"
    },

    // Homepage - Media Monitoring
    homepage: {
        title: "Media Monitoring",
        subtitle: "Configure your keywords to monitor relevant news",
        configureKeywords: "Configure Keywords",
        update: "Update",
        searching: "Searching...",
        customize: "Customize",
        country: "News country",
        countryHint: "Change the search country. News will reload automatically.",

        // Tabs
        tabNews: "News",
        tabSaved: "Saved",
        savedTitle: "Saved News",

        // TikTok
        tiktokTitle: "TikTok Posts",
        tiktokRefresh: "Refresh TikTok",
        tiktokSearching: "Searching...",
        tiktokPendingTitle: "Searching TikTok posts…",
        tiktokPendingHint: "This may take 3-8 minutes. You can switch tabs in the meantime.",
        tiktokStartedAt: "Started at",
        tiktokLastFetched: "Last updated:",
        tiktokEmpty: "No posts yet. Press Refresh to start the search.",
        tiktokEmptyNoKeywords: "Add keywords first — TikTok uses the same keywords as news.",
        savedTiktoksHeader: "Saved TikToks",
        tiktokSortLabel: "Sort by",
        tiktokSortViews: "Most viewed",
        tiktokSortRecent: "Most recent",
        tiktokSortOldest: "Oldest first",
        tiktokSortLikes: "Most liked",
        tiktokSortComments: "Most commented",
        // Relative post age
        agoPrefix: "",
        monthAgo: "month ago",
        monthsAgo: "months ago",
        yearAgo: "year ago",
        yearsAgo: "years ago",

        // Comments modal
        commentsTitle: "Comments",
        commentsClose: "Close",
        commentsViewOnTiktok: "View on TikTok",
        commentsLoadButton: "Load comments",
        commentsConfirmCount: "This post has {count} comments.",
        commentsCap: "We'll store up to {cap} comments (top by likes).",
        commentsCapExceeded: "Post exceeds {cap}. We'll store only top {cap} by likes.",
        commentsPending: "Loading comments... May take 3-8 minutes.",
        commentsRetry: "Retry",
        commentsErrorGeneric: "Could not load comments.",
        commentsErrorNetwork: "Network error loading comments.",
        commentsEmpty: "No comments available.",
        commentsLoadedMeta: "{count} comments loaded · {when}",
        commentsViewThread: "View {count} replies on TikTok",

        // Sentiment analysis
        sentimentTitle: "Sentiment analysis",
        sentimentCta: "Analyze sentiment",
        sentimentAnalyzing: "Analyzing...",
        sentimentReanalyze: "Re-analyze",
        sentimentThemes: "Main themes",
        sentimentTopPositive: "Top positive",
        sentimentTopNegative: "Top critical",
        sentimentErrorGeneric: "Analysis could not complete.",
        sentimentErrorNetwork: "Network error during analysis.",

        // Keywords panel
        keywordsConfig: "Keywords Configuration",
        keywordsConfigDesc: "Define keywords to monitor news",
        searchPlaceholder: "Type a keyword and press Enter...",
        useIndustryTemplate: "Use industry template",
        activeKeywords: "Active Keywords",
        resetDefault: "Default",
        clearAll: "Clear",
        loadingKeywords: "Loading keywords...",
        noKeywordsConfigured: "No keywords configured.",
        addKeywordsHint: "Add keywords manually or use a template.",
        lastSaved: "Last saved:",
        saveConfig: "Save Configuration",
        saving: "Saving...",
        keywordsLimitError: "Cannot add more than 20 keywords",
        keywordsLimitReached: "20 keywords limit reached",
        confirmRestoreDefaults: "Restore the organization's default keywords?",

        // Status bar
        news: "news",

        // News section
        relevantNews: "Relevant News",
        readArticle: "Read article",
        noLink: "No link",
        today: "Today",
        yesterday: "Yesterday",
        daysAgo: "days ago",

        // Loading & empty states
        searchingNews: "Searching news...",
        analyzingKeywords: "Analyzing {count} keywords",
        searchMayTake: "This may take 30-45 seconds",
        noNewsFound: "No news found",
        tryOtherKeywords: "Try other keywords or update later",
        searchNews: "Search news",

        // Presets
        presetsPharmaceutical: "Pharmaceutical",
        presetsRetail: "Retail",
        presetsTechnology: "Technology",
        presetsFinance: "Finance"
    },

    // Alerts Page - Price Monitor
    alerts: {
        title: "Price Monitor",
        subtitle: "Competitor price change alerts",
        update: "Update",
        info: "Information",

        // Config panel
        minThreshold: "Min absolute change (%)",
        maxThreshold: "Max absolute change (%)",
        productsPerChain: "Products per chain",
        filterByUpcs: "Filter by UPCs",
        enabled: "Enabled",
        disabled: "Disabled",
        apply: "Apply",
        upcs: "UPCs",

        // Summary
        alerts: "alerts",
        changeGte: "change",
        upcFilterActive: "UPC filter active",
        filterChannels: "Filter channels",

        // Product card
        previous: "Previous:",
        current: "Current:",
        viewHistory: "View history",
        viewInStore: "View in store",

        // History modal
        priceHistory: "Price History",
        priceEvolution: "Price evolution",
        lastRecords: "Last {count} records",
        date: "Date",
        regularPrice: "Regular Price",
        salePrice: "Sale Price",
        finalPrice: "Final Price",
        currentPrice: "Current price",
        detectedChange: "Detected change",

        // UPC Manager
        manageTrackedUpcs: "Manage Tracked UPCs",
        fileExists: "File exists with {count} UPCs",
        noTrackingFile: "No UPCs configured. Add UPCs to start monitoring.",
        currentUpcs: "Current UPCs",
        clear: "Clear",
        more: "more",
        loadFromFile: "Load from file",
        downloadTemplate: "Download CSV template",
        uploadFile: "Upload CSV file",
        orEnterManually: "Or enter manually",
        upcsHint: "UPCs separated by comma or one per line. Saving will update the S3 file.",
        cancel: "Cancel",
        replaceAll: "Replace all",
        addUpcs: "Add UPCs",
        save: "Save",
        upcsStorageInfo: "Saved UPCs will be used to filter price alerts. Threshold applies as absolute variation (± %).",

        // States
        analyzingPriceChanges: "Analyzing price changes...",
        noPriceAlerts: "No price alerts",
        noChangesDetected: "No price changes detected with variation between {min}% and {max}%",
        noChangesInTracked: "No price changes detected in your {count} tracked UPCs",
        retry: "Retry",
        noHistoryAvailable: "No price history available",
        lastRecordedDate: "Date not available",
        noUpcsConfigured: "No UPCs configured",
        viewAllProducts: "View all products",
        filterOnlyMyUpcs: "Filter only my UPCs",
        clearAllUpcs: "Delete all UPCs",
        replaceAllUpcs: "Replace all existing UPCs",

        // Info modal
        howToUse: "How to use Price Monitor",
        overviewTitle: "Overview",
        overviewDesc: "Shows products with the largest price changes by chain, useful for detecting significant market movements.",
        filterByUpcsTitle: "Filter by UPCs",
        filterByUpcsDesc: "Enable the 'Filter by UPCs' toggle to see only the products you've configured. To add UPCs:",
        filterStep1: "Click the 'UPCs' button",
        filterStep2: "Download the CSV template or upload your file",
        filterStep3: "UPCs are saved in your folder",
        understood: "Got it",

        // Sorting options
        sortBy: "Sort by",
        sortHighest: "Highest variation",
        sortLowest: "Lowest variation",
        sortPositive: "Increases only",
        sortNegative: "Decreases only",

        // Show all tracked UPCs
        showAllTracked: "Show all",
        showOnlyWithChanges: "Show only UPCs with price changes",
        showAllTrackedUpcs: "Show all priority UPCs (with or without changes)",
        onlyChanges: "With changes",

        // Channels
        selectChannels: "Select channels",
        all: "All",
        none: "None",

        // Email notifications
        emailNotifications: "Email notifications",
        emailNotificationsEnable: "Enable email notifications",
        emailNotificationsDisable: "Disable email notifications",

        // Threshold
        thresholdHint: "Minimum change (%). Press Enter or leave the field to apply.",
        minThresholdHint: "Minimum change % (absolute value). 0 includes products with no change. Enter or blur to apply.",
        maxThresholdHint: "Maximum change % (absolute value), filters outliers. E.g. max 10 = only what moved within ±10%.",
        changeBetween: "absolute change between {min}% and {max}%",

        // Variation basis (Final price vs Regular / full price)
        priceBasisLabel: "Variation on",
        basisFinal: "Final price",
        basisFull: "Regular price",
        basisFinalHint: "Variation computed on the final price (with promotions), using the precomputed previous price.",
        basisFullHint: "Variation computed on the regular (full) price. The previous price is computed on the fly, so it may take a bit longer.",

        // View selector
        viewMode: "View",
        viewGrid: "Cards",
        viewTable: "Table",

        // Table columns
        colChain: "Chain",
        colProduct: "Product",
        colPrevious: "Previous",
        colCurrent: "Current",
        colVariation: "Variation",
        downloadExcel: "Download Excel",
        downloadExcelHint: "Download the alerts you are viewing to Excel, with the current filters",
        excelSheetAlerts: "Alerts",
        excelSheetFilters: "Filters",
        excelFilter: "Filter",
        excelValue: "Value",
        excelRange: "Variation range",
        excelBasis: "Price basis",
        excelSort: "Sort",
        excelChains: "Chains",
        excelUpcFilter: "UPC filter",
        excelRows: "Exported alerts",
        excelDownloadedAt: "Downloaded at",
        excelYes: "Yes",
        excelNo: "No"
    },

    // Chat
    chat: {
        // Plataforma nueva (src/nueva)
        stop: "Stop",
        stopAnswer: "Stop response",
        stopped: "Response stopped",
        copyAnswer: "Copy response",
        copyCode: "Copy code",
        codeLabel: "code",
        mode: "Processing mode",
        modeNormal: "Normal",
        modeMax: "Maximum",
        modeNormalDesc: "Normal processing (Sonnet): fast and efficient",
        modeMaxDesc: "Maximum processing (Opus): higher quality, slower and more expensive",
        attach: "Attach file (image, Excel, CSV, PDF)",
        emptyTitle: "AI Assistant",
        emptyText: "Ask about your prices, competitors, sales or margins. I query your data, chart it, and you can save the answers to a report.",
        historyError: "We couldn't load the conversation.",
        historyNotFound: "This conversation doesn't exist or isn't yours.",
        retry: "Retry",
        title: "AI Assistant",
        placeholder: "Ask about your data · Type @ for files · /remember to train the agent",
        send: "Send",
        thinking: "Thinking...",
        error: "An error occurred. Please try again.",
        newConversation: "New conversation",
        clearChat: "Clear chat",
        exportChat: "Export chat",
        disclaimer: "While I analyze data quickly, the final decision is yours. My suggestions are only indicative, review carefully before acting.",

        // Suggested questions
        loadingSuggestions: "Generating suggestions...",
        suggestedQuestions: [
            "What are the products with the highest sales in units last month?",
            "Which categories have the best profit margins?",
            "How do our prices compare to the market by category?"
        ],

        // Data table
        rows: "rows",
        columns: "columns",
        export: "Export",
        visualize: "Advanced visualization",
        chartModalTitle: "Data visualization",
        viewData: "View Data",
        downloadExcel: "Download Excel",
        downloadCSV: "Download CSV",
        downloadPDF: "Download PDF",
        loadingData: "Loading data...",
        options: "Options",
        saving: "Saving...",
        saveQuery: "Save query",
        saveToReport: "Save to report",
        saveToReportTitle: "Save response to report",
        saveChartToReportTitle: "Save chart to report",
        createNewReportWithMessage: "Create a new report with this response",
        createNewReportWithChart: "Create a new report with this chart",
        orAddToExisting: "or add to an existing one:",
        loadingReports: "Loading reports…",
        noReportsYet: "You have no reports yet. Create one above.",
        blocksCount: "blocks",
        savedInReport: "Saved to",
        view: "View",
        chart: "Chart",
        download: "Download",
        generateChart: "Generate chart",
        downloadTableAsExcel: "Download table as Excel",

        // Chart
        chartType: "Chart type",
        bars: "Bars",
        lines: "Lines",
        pie: "Pie",
        doughnut: "Doughnut",
        dataConfig: "Data configuration",
        xAxis: "X Axis (Category)",
        yAxis: "Y Axis (Value)",
        aggregation: "Aggregation",
        sum: "Sum",
        average: "Average",
        count: "Count",
        max: "Maximum",
        min: "Minimum",
        dataLimit: "Data limit",
        allData: "All",
        categories: "categories",
        records: "records"
    },

    // Admin
    admin: {
        title: "Administration Panel",
        back: "Back",
        users: "Users",
        files: "Files",
        guide: "Guide",
        plan: "My Plan",
        terms: "Terms",
        faq: "FAQ",
        plans: "Plans",

        // User manager
        usersTitle: "Organization Users",
        configureMyMFA: "Set up my MFA",
        totalUsers: "Total Users",
        active7Days: "Active (7 days)",
        active30Days: "Active (30 days)",
        administrators: "Administrators",
        searchUsers: "Search users...",
        addUser: "Add User",
        editUser: "Edit User",
        deleteUser: "Delete User",
        username: "Username",
        organization: "Organization",
        role: "Role",
        lastAccess: "Last Access",
        pbiAccess: "PBI Access",
        messages: "Messages",
        registrationDate: "Registration",
        actions: "Actions",
        loadingUsers: "Loading users...",
        noUsersInOrg: "No users in this organization",
        never: "Never",
        justNow: "Just now",
        minutesAgo: "min ago",
        hoursAgo: "hours ago",
        yesterday: "Yesterday",
        daysAgo: "days ago",
        weeksAgo: "weeks ago",

        // Plan manager
        planTitle: "Plan and Monthly Usage",
        monthlyQueries: "Monthly Queries (Organization)",
        used: "used",
        remaining: "remaining",
        queries: "queries",
        period: "Period",
        usersInOrg: "Users",
        inOrg: "in org.",
        planDetails: "Plan Details",
        usersInOrganization: "Users in organization",
        monthlyLimit: "Monthly limit",
        queriesUsedThisMonth: "Queries used this month",
        planNote: "The counter shows queries from ALL users in the organization. It resets automatically on the first day of each month.",
        orgExceededLimit: "The organization has exceeded the monthly limit",
        queriesLeft: "queries left for the organization",
        loadingPlan: "Loading plan information...",
        noUsageData: "No usage data available",
        retry: "Retry",

        // File manager
        fileManagerTitle: "File Manager",
        filesCount: "files",
        total: "total",
        searchFiles: "Search files...",
        uploadFile: "Upload File",
        fileName: "File Name",
        fileType: "Type",
        size: "Size",
        uploadedBy: "Uploaded By",
        uploadDate: "Upload Date",
        download: "Download",
        delete: "Delete",
        noPermission: "You don't have permission to view files",
        errorLoadingFiles: "Error loading files",
        fileTypeNotAllowed: "File type not allowed. Use CSV, Excel, PDF or images.",
        fileSizeExceeded: "File exceeds the 500 MB limit per file",
        uploading: "Uploading...",
        fileUploadedSuccess: "File uploaded successfully",
        errorUploadingFile: "Error uploading file",
        connectionError: "Connection error while uploading file",
        errorDownloading: "Error generating download link",
        errorDeleting: "Error deleting file",
        fileDeletedSuccess: "File deleted successfully",
        dragDropHint: "Drag files here or click to select",
        fileTypesHint: "CSV, Excel, PDF, Images (max 500MB per file)",
        loadingFiles: "Loading files...",
        noFilesUploaded: "No files uploaded",
        uploadFirstFile: "Upload your first file using the area above",
        file: "File",
        type: "Type",
        uploaded: "Uploaded",
        confirmDelete: "Confirm deletion",
        confirmDeleteMessage: "Are you sure you want to delete",
        cancel: "Cancel"
    },

    // Interactive Guide
    guide: {
        // Plataforma nueva (src/nueva)
        noRolesConfigured: "No roles are configured for this guide.",
        noPhasesConfigured: "No phases are configured for this guide.",
        title: "Interactive Analysis Guide",
        subtitle: "Explore pricing and elasticity data with guided queries by role or analysis phase",
        byRole: "By Role",
        byPhase: "By Phase",
        progress: "Progress",
        completed: "completed",
        priority: "Priority",
        high: "High",
        medium: "Medium",
        low: "Low",
        estimatedTime: "Est. time",
        minutes: "min",
        runQuery: "Run",
        running: "Running...",
        viewFullData: "View full data",
        loadingData: "Loading data...",
        processingQuery: "Processing query...",
        close: "Close",
        queriesCount: "queries",

        // Roles
        roles: {
            ceo: "CEO",
            ceoDesc: "Global strategic vision of pricing and its impact on profitability",
            cfo: "CFO",
            cfoDesc: "Financial impact of pricing strategies",
            commercial: "Commercial Director",
            commercialDesc: "Growth opportunities and sales strategies",
            revenue: "Revenue Management Director",
            revenueDesc: "Revenue optimization and dynamic pricing management",
            marketing: "Marketing Director",
            marketingDesc: "Campaign effectiveness and positioning strategies",
            ecommerce: "eCommerce Manager",
            ecommerceDesc: "Online channel performance and digital pricing",
            priceAnalyst: "Price Analyst",
            priceAnalystDesc: "Detailed analysis of prices and competitive positioning",
            elasticityAnalyst: "Elasticity Analyst",
            elasticityAnalystDesc: "Price sensitivity and demand elasticity analysis"
        },

        // Phases
        phases: {
            exploration: "Data Exploration",
            explorationDesc: "Understand the structure and distribution of data",
            descriptive: "Descriptive Pricing Analysis",
            descriptiveDesc: "Statistics and metrics of current pricing",
            advanced: "Advanced Analytics",
            advancedDesc: "Predictive analysis and modeling",
            geographic: "Geographic Analysis",
            geographicDesc: "Regional distribution and local pricing strategies",
            optimization: "Price Optimization",
            optimizationDesc: "Recommendations and scenario simulations",
            export: "Export",
            exportDesc: "Generate reports and exports"
        }
    },

    // Reports
    reports: {
        // Plataforma nueva (src/nueva): «exportPdf» dice «Export PDF» también en es; ésta es la traducida.
        exportPdfButton: "Export PDF",
        title: "Reports & Analytics",
        loading: "Loading report...",
        noReports: "No reports available",
        selectReport: "Select a report to view",
        lastUpdate: "Last update",
        fullscreen: "Fullscreen",
        refresh: "Refresh",
        myFilters: "My Filters",
        savedFilters: "Saved Filters",
        saveCurrentFilters: "Save Current Filters",
        filterName: "Filter name",
        filterNamePlaceholder: "E.g: 'Q1 2024 - North Region'",
        filterDescription: "Description (optional)",
        saving: "Saving...",
        apply: "Apply",
        deleteFilter: "Delete filter",
        noFilters: "You don't have any saved filters yet.",
        noFiltersHint: "Adjust the report filters and save them above.",
        filterSaved: "Filters saved successfully",
        filterApplied: "Filters applied",
        filterDeleted: "Filter deleted",
        filterError: "Error saving filters",
        enterFilterName: "Enter a filter name",
        resetFilters: "Reset Filters",
        exportPdf: "Export PDF",
        default: "Default",
        savedOn: "Saved"
    },

    // Common
    common: {
        loading: "Loading...",
        error: "Error",
        success: "Success",
        confirm: "Confirm",
        cancel: "Cancel",
        save: "Save",
        saving: "Saving...",
        delete: "Delete",
        edit: "Edit",
        add: "Add",
        search: "Search",
        filter: "Filter",
        clear: "Clear",
        close: "Close",
        back: "Back",
        next: "Next",
        yes: "Yes",
        no: "No",
        ok: "OK",
        noData: "No data available",
        required: "Required",
        optional: "Optional"
    },

    // User Profile
    profile: {
        title: "User Profile",
        email: "Email",
        organization: "Organization",
        role: "Role",
        plan: "Plan",
        features: "Features",
        logout: "Logout",
        settings: "Settings"
    },

    // Floating Chat
    floatingChat: {
        // Plataforma nueva (src/nueva)
        greetingTitle: "Hi, I'm your AI Assistant",
        greetingText: "Ask me about prices, promotions or scenarios. I give you alternatives; the decision is yours.",
        suggestions: "Suggested questions",
        moreSuggestions: "More suggestions",
        lessSuggestions: "Fewer suggestions",
        levelBasic: "Basic",
        levelMedium: "Intermediate",
        levelAdvanced: "Advanced",
        online: "Online",
        openInChat: "Open in AI Pricing Engine",
        close: "Close AI Assistant",
        title: "AI Assistant",
        welcome: "Hello! I'm your **AI Pricing Analytics Engine**. I can help you query information from the datalake, analyze competitive pricing, and generate insights.\n\n**Available features:**\n- Data analysis\n- Image analysis (charts, tables, etc.)\n- Excel file processing\n- PDF document analysis\n\nWhat would you like to know?",
        cleared: "Chat cleared. I'm your **AI Pricing Analytics Engine**. How can I help you?",
        save: "Save conversation",
        expand: "Expand",
        restore: "Restore size",
        clear: "Clear chat",
        minimize: "Minimize",
        attach: "Attach file",
        screenshot: "Capture screen",
        analyzing: "Analyzing...",
        // Screenshot guide
        screenshotGuideTitle: "Screen Capture",
        screenshotGuideText: "A browser dialog will appear to select what you want to share.",
        screenshotStep1: "Select \"This Tab\" in the dialog",
        screenshotStep2: "Click \"Share\" to capture",
        screenshotStep3: "Add your question and send",
        cancel: "Cancel",
        continueCapture: "Continue"
    },

    // Errors
    errors: {
        generic: "An error occurred. Please try again.",
        network: "Network error. Check your connection.",
        unauthorized: "Unauthorized. Please log in again.",
        notFound: "Resource not found.",
        serverError: "Server error. Please try again later."
    },

    // Terms and Conditions
    tyc: {
        title: "Terms and Conditions",
        sections: [
            {
                title: "1. Introduction",
                content: "These Terms and Conditions govern the use of the AI Pricing Analytics Assistant. By using this service, you agree to comply with all the conditions described herein. Please note that suggested prices are only a guide and should be validated by the end user."
            },
            {
                title: "2. Use of Service",
                content: "The assistant provides recommendations based on data. Although it strives to be accurate, we do not guarantee absolute accuracy of results. Suggested prices should be considered as a guide and it is the end user's responsibility to validate them before implementation."
            },
            {
                title: "3. Privacy and Security",
                content: "The privacy and security of your data are inherited from the SLAs, rules, and security policies of AWS and the services used in the agent's architecture, such as S3, Lambda, PostgreSQL, and Bedrock. Additionally, data collected through the assistant is used exclusively to improve the accuracy of recommendations. We do not share your data with third parties without your explicit consent."
            },
            {
                title: "4. Limitation of Liability",
                content: "We will not be liable for losses or damages arising from the use of the assistant. The end user is responsible for validating suggested prices before making business decisions."
            },
            {
                title: "5. Modifications",
                content: "We reserve the right to modify these terms at any time. Modifications will be effective from the moment of their publication on this page."
            },
            {
                title: "6. Contact",
                content: "For any questions or clarifications about these terms, you can contact us through your Advisor."
            }
        ]
    },

    // FAQ
    faq: {
        title: "Frequently Asked Questions about AI Pricing Analytics Assistant",
        questions: [
            {
                question: "What is the AI Pricing Analytics Assistant?",
                answer: "It is an artificial intelligence-powered tool that helps you optimize your product pricing strategy by analyzing sales, margins, competitor prices, and other key factors. Remember that suggested prices are only a guide and should be validated by the end user."
            },
            {
                question: "What type of data is used?",
                answer: "We use data such as sale date, product identifiers (SKU), units sold, final prices, margins, and other parameters related to competition and geographic locations."
            },
            {
                question: "How is the accuracy of recommended prices ensured?",
                answer: "The assistant uses machine learning models that are constantly trained with updated data to offer accurate and evidence-based recommendations. However, you should always validate the recommendations before implementing them."
            },
            {
                question: "How is my data privacy protected?",
                answer: "The privacy and security of your data are our priority. These are inherited from the SLAs, rules, and security policies of AWS and the services we use in the assistant's architecture, such as S3, Lambda, PostgreSQL, and Bedrock. Additionally, they are managed in accordance with current laws and regulations, using encryption and other advanced security measures."
            },
            {
                question: "Can I customize the price recommendations?",
                answer: "Yes, you can adjust the parameters and criteria that the assistant uses so that the recommendations better adapt to your specific needs. Even so, suggested prices should be validated before applying them."
            }
        ]
    },

    // Guide Queries - Roles
    guideQueries: {
        roles: {
            ceo: {
                title: "CEO",
                description: "Global strategic vision of pricing and its impact on profitability",
                queries: [
                    { text: "Show me an executive summary with the most important pricing KPIs and their impact on overall profitability", category: "summary" },
                    { text: "What is the financial impact of our pricing strategies vs competition?", category: "competition" },
                    { text: "Sales trend analysis by region and category in the last 6 months", category: "trends" }
                ]
            },
            cfo: {
                title: "CFO",
                description: "Financial impact of pricing strategies",
                queries: [
                    { text: "Financial impact of AI-optimized pricing strategies", category: "financial" },
                    { text: "Price sensitivity analysis and its effect on contribution margin", category: "sensitivity" },
                    { text: "Financial projections based on different pricing scenarios", category: "projections" }
                ]
            },
            commercial: {
                title: "Commercial Director",
                description: "Growth opportunities and sales strategies",
                queries: [
                    { text: "What are the main growth opportunities identified in the data?", category: "opportunities" },
                    { text: "Price elasticity analysis by customer segment and sales channel", category: "elasticity" },
                    { text: "Cross-promotion recommendations to maximize customer value", category: "promotions" },
                    { text: "Sales performance by channel and region with trends", category: "performance" }
                ]
            },
            revenue: {
                title: "Revenue Management Director",
                description: "Revenue optimization and dynamic pricing management",
                queries: [
                    { text: "Pricing performance dashboard with alerts and recommendations", category: "dashboard" },
                    { text: "Analysis of the effectiveness of implemented pricing rules", category: "effectiveness" },
                    { text: "Price war scenario simulation and response strategies", category: "simulation" }
                ]
            },
            marketing: {
                title: "Marketing Director",
                description: "Campaign effectiveness and positioning strategies",
                queries: [
                    { text: "Analysis of marketing campaign effectiveness in relation to pricing strategies", category: "campaigns" },
                    { text: "Price recommendations for new product launches", category: "launches" },
                    { text: "Customer segmentation based on price sensitivity and buying behavior", category: "segmentation" }
                ]
            },
            ecommerce: {
                title: "eCommerce Manager",
                description: "Digital channel optimization",
                queries: [
                    { text: "Real-time price optimization for the online channel", category: "online" },
                    { text: "Analysis of buying behavior in relation to prices", category: "behavior" },
                    { text: "Personalized pricing strategies for different online customer segments", category: "personalization" }
                ]
            },
            priceAnalyst: {
                title: "Price Analyst",
                description: "Detailed analysis and price modeling",
                queries: [
                    { text: "Predictive modeling tools for price scenario analysis", category: "modeling" },
                    { text: "Detailed price elasticity reports by SKU and category", category: "elasticity" },
                    { text: "Automatic alerts for price anomalies or buying behavior", category: "anomalies" },
                    { text: "Give me a table with the top 20 products by contribution margin", category: "top_products" }
                ]
            },
            categories: {
                title: "Category Director",
                description: "Portfolio management and category optimization",
                queries: [
                    { text: "Margin contribution analysis by product, category and customer", category: "contribution" },
                    { text: "Optimization of discount and promotion structure", category: "discounts" },
                    { text: "Identification of profitability improvement opportunities by category", category: "profitability" }
                ]
            }
        },
        phases: {
            eda: {
                title: "Data Exploration (EDA)",
                description: "Initial exploratory analysis of available data",
                queries: [
                    { text: "Give me a table with the main sales trends by category", category: "trends" },
                    { text: "What columns and metrics are available in the data?", category: "structure" },
                    { text: "Statistical summary of price and sales data", category: "statistics" },
                    { text: "What time period does the available data cover?", category: "period" }
                ]
            },
            pricing: {
                title: "Descriptive Pricing Analysis",
                description: "Detailed analysis of current pricing strategies",
                queries: [
                    { text: "Price distribution by category and competitor", category: "distribution" },
                    { text: "Price gap analysis vs competition by category", category: "gaps" },
                    { text: "Historical price evolution by product and brand", category: "evolution" },
                    { text: "Margin comparison by sales channel", category: "margins" }
                ]
            },
            elasticity: {
                title: "Advanced Elasticity Analytics",
                description: "Price sensitivity and demand elasticity analysis",
                queries: [
                    { text: "Price-demand elasticity calculation by category", category: "elasticity" },
                    { text: "Products with highest price sensitivity", category: "sensitivity" },
                    { text: "Impact of price changes on sales volume", category: "impact" },
                    { text: "Correlation between price and market share", category: "correlation" }
                ]
            },
            geographic: {
                title: "Geographic & Competitiveness Analysis",
                description: "Regional analysis and competitive positioning",
                queries: [
                    { text: "Sales performance by geographic region", category: "regional" },
                    { text: "Price competitiveness analysis by zone", category: "competitiveness" },
                    { text: "Market opportunities by location", category: "opportunities" }
                ]
            },
            optimization: {
                title: "Price Optimization",
                description: "Analysis-based optimization recommendations",
                queries: [
                    { text: "Price adjustment recommendations to maximize margin", category: "recommendations" },
                    { text: "Price change impact simulation", category: "simulation" },
                    { text: "Optimal pricing strategy by segment", category: "strategy" },
                    { text: "Expected ROI from proposed optimizations", category: "roi" }
                ]
            },
            export: {
                title: "Recommendations Export",
                description: "Report generation and data export",
                queries: [
                    { text: "Generate an executive report with the main metrics", category: "report" },
                    { text: "List of products with price change recommendation", category: "list" },
                    { text: "Summary of key findings for presentation", category: "findings" }
                ]
            }
        }
    },

    // MFA / TOTP setup
    mfa: {
        title: "Two-factor authentication (MFA)",
        loading: "Loading configuration...",
        scanInstructions: "Scan the QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, 1Password, etc.).",
        cantScan: "Can't scan the QR?",
        manualSecret: "Enter this code manually in your authenticator app:",
        copy: "Copy",
        copied: "Copied",
        next: "Next",
        back: "Back",
        cancel: "Cancel",
        close: "Close",
        retry: "Retry",
        enterCode: "Enter the 6-digit code shown in your authenticator app:",
        verify: "Verify",
        verifying: "Verifying...",
        processing: "Processing...",
        successTitle: "MFA enabled!",
        successDesc: "Next time you sign in we'll ask for the 6-digit code from your authenticator app.",
        nextLogin: "You'll need your authenticator app every time you sign in.",
        alreadyEnabled: "MFA is already enabled on your account",
        alreadyEnabledDesc: "You're using an authenticator app. You can reconfigure it (for example, when switching devices) or remove it entirely.",
        reconfigure: "Reconfigure MFA",
        remove: "Remove MFA",
        removeConfirmTitle: "Remove MFA?",
        removeConfirmDesc: "Your account will no longer have two-factor authentication. Anyone with your password will be able to sign in. You can re-enable MFA at any time.",
        confirmRemove: "Yes, remove",
        removedTitle: "MFA removed",
        removedDesc: "You will no longer be asked for an authenticator code when signing in. We recommend re-enabling MFA when possible.",
        errorGeneric: "Something went wrong. Please try again.",
        errorInvalidCode: "Invalid code. Check the code in your authenticator app and try again."
    },

    // Custom Reports (editor)
    report: {
        // Plataforma nueva (src/nueva)
        saveError: "Couldn't save",
        printPreview: "Print preview · A4",
        printTitle: "Report to print",
        printOrSave: "Print / Save as PDF",
        close: "Close",
        deleteReportTitle: "Delete report",
        untitled: "Untitled report",
        loading: "Loading report…",
        titlePlaceholder: "Report title",
        saving: "Saving…",
        exportPdf: "PDF",
        exportPdfTitle: "Export to PDF (Print → Save as PDF)",
        deleteReport: "Delete",
        deleteConfirm: "Delete this report? This cannot be undone.",
        empty: "This report is empty.",
        emptyHint: "Add blocks from the bar below, or go to the chat and use the \"Save to report\" button on any message.",
        addBlockLabel: "+ Add block:",
        blockHeading: "Heading",
        blockText: "Text",
        blockDivider: "Divider",
        newHeading: "New heading",
        newText: "Type here…",
        emptyPlaceholder: "*Empty — click to edit*",
        botMessageLabel: "Agent response",
        clientMessageLabel: "Question",
        moveUp: "Move up",
        moveDown: "Move down",
        delete: "Delete",
        untitledHeading: "Untitled",
        markdownPlaceholder: "Write in markdown…",
        blockChartAlt: "Chart",
        chartCaptionPlaceholder: "Chart caption (optional)",
        placeholder: "Start writing, or use the buttons above to insert elements…",
        savedJustNow: "Saved",
        toolbar: {
            // Plataforma nueva (src/nueva)
            deleteImage: "Delete chart/image",
            heading: "Text style",
            paragraph: "Paragraph",
            h1: "Heading 1",
            h2: "Heading 2",
            h3: "Heading 3",
            insertTable: "Insert table",
            deleteTable: "Delete table"
        }
    }
};

export default en;
